import { useState, useEffect, useCallback } from 'react';
import type { ApiResponse } from '../constants/types';
import type { BannerItem, CachedData } from '../constants/types/banner-type';
import { cacheUtils } from '../util/cache';
import instance from '../api/axios/utils.ts/instance';
import { isWithin } from '../api/utils/util-func';
import { BANNER_CATEGORY } from '../constants/bannerCategory';

/**
 * 배너 데이터를 관리하는 커스텀 훅
 *
 * 주요 기능:
 * 1. 배너 데이터 fetch 및 캐싱
 * 2. ETag를 이용한 HTTP 캐싱
 * 3. 배너 클릭 핸들링
 * 4. 유효 기간 기반 배너 필터링
 *
 * @returns {Object} 배너 관련 상태와 핸들러들
 */
export const useBanners = (categoryId: BANNER_CATEGORY) => {
  // 초기 상태는 캐시된 데이터로 설정
  const [banners, setBanners] = useState<BannerItem[]>(() => {
    const cachedData = cacheUtils.getBannerCache();
    return cachedData?.banners ?? [];
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  /**
   * 배너 클릭 시 링크 타입에 따른 처리
   * SAME_TAB: 현재 탭에서 이동
   * NEW_TAB: 새 탭에서 열기
   * NONE: 아무 동작 안 함
   */
  const handleBannerClick = useCallback((banner: BannerItem) => {
    if (!banner.linkUrl) return;

    switch (banner.linkType) {
      case 'SAME_TAB':
        window.location.href = banner.linkUrl;
        break;
      case 'NEW_TAB':
        // noopener,noreferrer: 보안 취약점 방지
        window.open(banner.linkUrl, '_blank', 'noopener,noreferrer');
        break;
      case 'NONE':
        break;
      default:
        console.warn('알 수 없는 링크 타입:', banner.linkType);
    }
  }, []);

  /**
   * 배너 데이터를 서버로부터 가져오는 함수
   * 1. ETag 기반 캐싱 처리
   * 2. 에러 시 캐시된 데이터 사용
   * 3. 새로운 데이터 수신 시 캐시 업데이트
   */
  const fetchBanners = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const cachedEtag = cacheUtils.getEtagCache();

      // ETag를 이용한 조건부 요청
      const response = await instance.get<ApiResponse<BannerItem[]>>(
        '/v1/public/banners',
        {
          params: {
            categoryId: categoryId,
          },
          headers: {
            ...(cachedEtag ? { 'If-None-Match': cachedEtag } : {}),
          },
          // 304 상태코드도 정상 응답으로 처리
          validateStatus: (status) =>
            (status >= 200 && status < 300) || status === 304,
        },
      );

      // 304: Not Modified - 캐시된 데이터 사용
      if (response.status === 304) {
        const cachedData = cacheUtils.getBannerCache();
        if (cachedData) {
          setBanners(cachedData.banners);
        }
        return;
      }

      // 새로운 데이터 처리
      const newBanners = response.data.data ?? [];
      const newEtag = response.headers.etag;

      // 새로운 ETag 저장
      if (newEtag) {
        cacheUtils.setEtagCache(newEtag);
      }

      // 새로운 데이터 캐싱
      const cacheData: CachedData = {
        banners: newBanners,
        timestamp: Date.now(),
      };

      cacheUtils.setBannerCache(cacheData);
      setBanners(newBanners);
    } catch (err) {
      // 에러 처리 및 폴백으로 캐시 데이터 사용
      setError(
        err instanceof Error
          ? err
          : new Error('배너 데이터를 불러오는데 실패했습니다.'),
      );
      const cachedData = cacheUtils.getBannerCache();
      if (cachedData) {
        setBanners(cachedData.banners);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // 컴포넌트 마운트 시 데이터 fetch
  useEffect(() => {
    fetchBanners();
  }, [fetchBanners]);

  /**
   * 배너의 유효 기간을 체크하는 함수
   * fromDate와 toDate가 없으면 항상 유효한 것으로 처리
   */
  const isDateValid = (item: BannerItem) => {
    const { fromDate, toDate } = item;
    if (!fromDate || !toDate) return true;
    return isWithin(fromDate, toDate);
  };

  // 현재 유효한 배너만 필터링
  const filteredBanners = banners.filter(isDateValid);

  return {
    banners: filteredBanners, // 유효 기간 내의 배너만 반환
    loading,
    error,
    handleBannerClick,
    refetch: fetchBanners, // 수동 새로고침용 함수 제공
  };
};
